'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { AuthEventKeys, AuthEvents, LoginScreen } from '@/auth';
import { createEventBus, EventBus, getFirebaseAuth } from '@/shared/utils';
import { BackgroundWavesScreen, LoadingSpinner } from '@/shared/ui';
import { Auth as AuthDict } from '@/shared/i18n';
import { useFirebaseAuth } from '@/shared/hooks';
import { useRouter } from 'next/navigation';

interface RegisterActivateContainerProps {
  dict: AuthDict; // Definisce la prop dict come di tipo Auth
}
function RegisterActivateContainer({ dict }: RegisterActivateContainerProps) {
  const authEventBus = createEventBus<AuthEvents>('auth-scope');
  const [viewReady, setViewReady] = useState(false); // Stato per controllare il rendering
  const [oobCode, setOobCode] = useState<string | null>(null);
  const firebaseAuth = useFirebaseAuth();
  const router = useRouter();

  // Funzione di callback per gestire l'evento e attivare il rendering
  const handleRegisterActivateView = useCallback((): void => {
    setViewReady(true);
  }, [setViewReady]);

  const handleGoLogin = () => {
    router.push('/auth/login');
  };

  useEffect(() => {
    authEventBus.on(AuthEventKeys.login.go, handleGoLogin);

    if (!firebaseAuth) return; // Aspetta che auth sia inizializzato
    const code = new URLSearchParams(window.location.search).get('oobCode');
    if (code) {
      setOobCode(code);
      authEventBus.emit('auth/activate/do', { activationCode: code, auth: firebaseAuth });
    } else {
      console.error('Missing oobCode in activation URL');
    }

    // Emettiamo l'evento per la vista di attivazione
    authEventBus.emit(AuthEventKeys.activate.view, undefined);

    return () => {
      authEventBus.off(AuthEventKeys.activate.do, handleRegisterActivateView);
      authEventBus.off(AuthEventKeys.login.go, handleGoLogin);
    };
  }, [authEventBus, handleRegisterActivateView]);

  if (!firebaseAuth || !oobCode) {
    // Puoi mostrare un loader o un messaggio di errore
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh', // L'altezza prende tutto lo schermo
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  // Se la pagina non è ancora pronta, mostriamo un blocco placeholder nero
  if (!viewReady) {
    return (
      <>
        <div className="flex z-10 flex-col self-center max-w-[600px] w-full gap-4">
          <LoginScreen eventBus={authEventBus} dict={dict} auth={firebaseAuth} />
        </div>
        <BackgroundWavesScreen eventBus={authEventBus} />
      </>
    );
  }

  return (
    <>
      <div className="flex z-10 flex-col self-center max-w-[600px] w-full gap-4">
        <LoginScreen eventBus={authEventBus} dict={dict} auth={firebaseAuth} />
      </div>
      <BackgroundWavesScreen eventBus={authEventBus} />
    </>
  );
}

export default RegisterActivateContainer;
